import React, { useCallback } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import TextField from 'modules/views/shared/TextField';
import MobileInput from 'modules/views/shared/MobileInput';

import { IProfileInformationSchema } from '../schema';
import { useStyles } from '../styles';

interface IContactField {
  id: string;
  name: string;
  isRequired: boolean;
}

interface IContactInfoSectionProps {
  contactInfoFields: {
    email: IContactField;
    mobileNumber: IContactField;
    phoneNumber: IContactField;
  };
  values: Pick<IProfileInformationSchema, 'email' | 'mobileNumber' | 'phoneNumber'>;
  errors: Record<string, { message: string } | undefined>;
  setValue: (key: keyof IProfileInformationSchema, value: any) => void;
  canEditEmail: boolean;
  canEditMobilePhoneNumber: boolean;
}

const ContactInfoSection: React.FC<IContactInfoSectionProps> = ({
  contactInfoFields,
  values,
  errors,
  setValue,
  canEditEmail,
  canEditMobilePhoneNumber,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const handlePhoneChange = useCallback(
    (field: keyof IProfileInformationSchema) =>
      ({ phone }: { phone: string; inputValue: string; country: any }) => {
        setValue(field, phone);
      },
    [setValue],
  );

  return (
    <>
      <p className={classes.sectionTitle} data-testid="profile-section-title-contact-info">
        {t('contactInformation.contactInfo.title')}
      </p>
      <Grid container spacing={3} data-testid="profile-section-contact-info">
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label={contactInfoFields.email.name}
            name="email"
            testId="email-input"
            required={contactInfoFields.email.isRequired}
            value={values.email}
            error={errors.email?.message}
            disabled={!canEditEmail}
            onChange={e => setValue('email', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <MobileInput
            label={contactInfoFields.mobileNumber.name}
            testId="mobileNumber-input"
            required={contactInfoFields.mobileNumber.isRequired}
            value={values.mobileNumber}
            error={errors.mobileNumber?.message}
            disabled={!canEditMobilePhoneNumber}
            onChange={handlePhoneChange('mobileNumber')}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <MobileInput
            label={contactInfoFields.phoneNumber.name}
            testId="phoneNumber-input"
            required={contactInfoFields.phoneNumber.isRequired}
            value={values.phoneNumber}
            error={errors.phoneNumber?.message}
            onChange={handlePhoneChange('phoneNumber')}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default ContactInfoSection;
